/* import __COLOCATED_TEMPLATE__ from './create.hbs'; */
import { task } from 'ember-concurrency';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { HTMLElementEvent } from 'common/utils/forms';
import type ApiService from 'api/services/api';
import type {
  HashicorpCloudResourcemanagerOrganization,
  HashicorpCloudResourcemanagerProject,
} from '@clients/cloud-resource-manager';
import type {
  HashicorpCloudNetwork20200907CreateDNSForwardingRuleRequest as CreateDNSForwardingRuleRequest,
  HashicorpCloudNetwork20200907CreateDNSForwardingRequest as CreateDNSForwardingRequest,
  HashicorpCloudNetwork20200907DNSForwarding as DNSForwarding,
  HashicorpCloudNetwork20200907Network as Network,
  HashicorpCloudNetwork20200907Peering as Peering,
  HashicorpCloudNetwork20200907TGWAttachment as TGWAttachment,
} from '@clients/cloud-network';
import type RouterService from '@ember/routing/router-service';
import type FlashMessagesService from 'ember-cli-flash/services/flash-messages';
import type { IntlService } from 'ember-intl';
import {
  FieldViolation,
  FormValidationError,
} from 'core/utils/form-validation';
import type OperationService from 'core/services/operation';
import { getOwner } from '@ember/owner';
import type Route from '@ember/routing/route';
interface Args {
  model: {
    network: Network;
    project: HashicorpCloudResourcemanagerProject;
    organization: HashicorpCloudResourcemanagerOrganization;
    dnsForwardings: DNSForwarding[];
    peerings: Peering[];
    tgwAttachments: TGWAttachment[];
  };
}

interface PeeringOption extends Peering {
  optionString: string;
  connectionType: 'tgw-attachment' | 'hvn-peering';
}

interface TGWAttachmentOption extends TGWAttachment {
  optionString: string;
  connectionType: 'tgw-attachment' | 'hvn-peering';
}

export default class PageHvnDnsForwardingCreate extends Component<Args> {
  @service declare readonly api: ApiService;
  @service declare readonly router: RouterService;
  @service declare readonly flashMessages: FlashMessagesService;
  @service declare readonly intl: IntlService;
  @service declare readonly operation: OperationService;

  @tracked connectionType: string | undefined;
  @tracked connection: string | undefined;
  @tracked IPs: string[] = [];
  @tracked domain = '';
  @tracked validationError: FormValidationError | null = null;
  @tracked ipsFieldIsPristine = true;
  @tracked domainFieldIsPristine = true;
  @tracked connectionFieldIsPristine = true;
  @tracked isForwardPrivateDomainBannerVisible = true;

  get provider() {
    if (this.args.model.network?.providerNetworkData?.awsNetworkData) {
      return 'aws';
    }
    return 'azure';
  }

  get connections() {
    const { peerings, tgwAttachments } = this.args.model;
    let peeringOptions: PeeringOption[] = [];
    let tgwAttachmentOptions: TGWAttachmentOption[] = [];
    if (peerings) {
      peeringOptions = peerings.map((peering) => {
        return {
          ...peering,
          connectionType: 'hvn-peering',
          optionString: `${this.intl.t('components.page.dns-forwarding-rules.create.fields.connection.peering')} ${peering.id}`,
        };
      });
    }
    if (tgwAttachments) {
      tgwAttachmentOptions = tgwAttachments.map((attachment) => {
        return {
          ...attachment,
          connectionType: 'tgw-attachment',
          optionString: `${this.intl.t('components.page.dns-forwarding-rules.create.fields.connection.tgw')} ${attachment.id}`,
        };
      });
    }
    return [...peeringOptions, ...tgwAttachmentOptions];
  }

  get hasExistingDnsForwardings() {
    return Boolean(this.args.model.dnsForwardings.length);
  }

  get dnsForwarding() {
    return this.args.model.dnsForwardings[0] || {};
  }

  get connectionId() {
    if (this.hasExistingDnsForwardings) {
      return this.args.model.dnsForwardings[0]?.peeringId;
    }
  }

  get existingConnection() {
    const { peerings, tgwAttachments } = this.args.model;
    if (!this.hasExistingDnsForwardings) return;
    const connections =
      this.dnsForwarding.connectionType === 'tgw-attachment'
        ? tgwAttachments
        : peerings;
    return connections.find((item) => item.id === this.dnsForwarding.peeringId);
  }

  @action hideForwardPrivateDomainBanner() {
    this.isForwardPrivateDomainBannerVisible = false;
  }

  @action updateConnection(value: string) {
    const connectionOption = this.connections.filter(
      (item) => item.id === value
    )[0];
    this.connectionType = connectionOption?.connectionType;
    this.connection = connectionOption?.id;
    this.connectionFieldIsPristine = false;
    this.validateForm();
  }

  @action updateIPs(event: HTMLElementEvent<HTMLInputElement>) {
    this.IPs = event.target.value
      .split(',')
      .map((ip) => ip.trim())
      .filter((str) => str);
    this.ipsFieldIsPristine = false;
    this.validateForm();
  }

  @action updateDomain(event: HTMLElementEvent<HTMLInputElement>) {
    this.domain = event.target.value?.trim();
    this.domainFieldIsPristine = false;
    this.validateForm();
  }

  validateIPs() {
    const errors = [] as FieldViolation[];
    const ipRegex = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/;
    const validIPs = this.IPs.filter((ip) => ip.match(ipRegex));
    if (!this.IPs.length) {
      errors.push(
        new FieldViolation(
          this.intl.t(
            'components.page.dns-forwarding-rules.create.errors.ips.blank'
          ),
          'ips'
        )
      );
    }
    if (validIPs.length !== this.IPs.length) {
      errors.push(
        new FieldViolation(
          this.intl.t(
            'components.page.dns-forwarding-rules.create.errors.ips.invalid'
          ),
          'ips'
        )
      );
    }
    if (this.IPs.length > 5) {
      errors.push(
        new FieldViolation(
          this.intl.t(
            'components.page.dns-forwarding-rules.create.errors.ips.maximum'
          ),
          'ips'
        )
      );
    }
    return errors;
  }

  validateDomain() {
    const errors = [] as FieldViolation[];
    if (!this.domain.length) {
      errors.push(
        new FieldViolation(
          this.intl.t(
            'components.page.dns-forwarding-rules.create.errors.domain.blank'
          ),
          'domain'
        )
      );
    }
    const domainSplit = this.domain.split('.');
    const validDomainParts = domainSplit.filter((domainPart: string, index) => {
      if (index === domainSplit.length - 1) {
        const tldReg = /[a-z]*/i;
        return tldReg.test(domainPart);
      } else {
        const domainReg = /^([a-z])([a-z0-9|-]*)([a-z0-9])$/i;
        return domainReg.test(domainPart);
      }
    });
    if (
      validDomainParts.length !== domainSplit.length ||
      domainSplit.length === 1
    ) {
      errors.push(
        new FieldViolation(
          this.intl.t(
            'components.page.dns-forwarding-rules.create.errors.domain.invalid'
          ),
          'domain'
        )
      );
    }
    return errors;
  }

  @action validateForm() {
    const errors = [] as FieldViolation[];
    if (!this.ipsFieldIsPristine) {
      const ipErrors = this.validateIPs();
      errors.push(...ipErrors);
    }

    if (!this.domainFieldIsPristine) {
      const domainErrors = this.validateDomain();
      errors.push(...domainErrors);
    }

    if (!this.connectionFieldIsPristine && !this.hasExistingDnsForwardings) {
      if (!this.connection) {
        errors.push(
          new FieldViolation(
            this.intl.t(
              'components.page.dns-forwarding-rules.create.errors.connection.blank'
            ),
            'connection'
          )
        );
      }
    }

    if (errors.length) {
      this.validationError = new FormValidationError('Validation error');
      this.validationError.details = errors;
    } else {
      this.validationError = null;
    }
  }

  @action
  refreshRoute() {
    const route = getOwner(this)!.lookup(
      `route:cloud.orgs.detail.projects.detail.hvns.detail`
    ) as Route;
    return route.refresh();
  }

  save = task(
    { drop: true },
    async (event: HTMLElementEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const { organization, project, network, dnsForwardings } =
        this.args.model;
      const orgId = organization.id;
      const projectId = project.id;
      const networkId = network.id;

      this.domainFieldIsPristine = false;
      this.ipsFieldIsPristine = false;
      this.connectionFieldIsPristine = false;
      this.validateForm();

      if (!orgId || !projectId || !networkId || this.validationError) {
        return;
      }

      let operation;

      if (this.hasExistingDnsForwardings) {
        const dnsForwardingId = dnsForwardings[0]?.id as string;
        const body = {
          dnsForwardingRule: {
            hvn: network,
            dnsForwardingId,
            rule: {
              inboundEndpointIps: this.IPs,
              domainName: this.domain,
            },
          },
        } as CreateDNSForwardingRuleRequest;
        const resp = await this.api.network.createDNSForwardingRule(
          orgId,
          projectId,
          networkId,
          dnsForwardingId,
          body
        );
        operation = resp.operation;
      } else {
        const body = {
          dnsForwarding: {
            hvn: network,
            connectionType: this.connectionType,
            peeringId: this.connection,
            rule: {
              inboundEndpointIps: this.IPs,
              domainName: this.domain,
            },
          },
        } as CreateDNSForwardingRequest;
        const resp = await this.api.network.createDNSForwarding(
          orgId,
          projectId,
          networkId,
          body
        );
        operation = resp.operation;
      }
      this.operation.addOperation(operation);
      this.refreshRoute();
      this.router.transitionTo(
        'cloud.orgs.detail.projects.detail.hvns.detail.dns-forwarding-rules'
      );
      this.flashMessages.success(
        this.intl.t(
          'components.page.dns-forwarding-rules.create.success.title'
        ),
        {
          content: this.intl.t(
            'components.page.dns-forwarding-rules.create.success.body'
          ),
        }
      );
    }
  );
}
